'use client'

import { Calendar as CalendarIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { MButton } from '@/_components/medante/ui/m-button'
import { IEmployeeAmbulance, IEmployeeExtended, TEAM_CATEGORY_TYPE_ENUM } from '@/_types'
import { SECTION_THEME_ENUM } from '@/_types/base'

type OrderButton = {
	label: string
	url: string
}

function partitionAmbulances(ambulances: IEmployeeAmbulance[]) {
	const childAmbulances: IEmployeeAmbulance[] = []
	const adultAmbulances: IEmployeeAmbulance[] = []

	for (const ambulance of ambulances) {
		if (ambulance.rootCategory?.sectionTheme === SECTION_THEME_ENUM.CHILD) {
			childAmbulances.push(ambulance)
		} else if (ambulance.rootCategory?.sectionTheme === SECTION_THEME_ENUM.ADULT) {
			adultAmbulances.push(ambulance)
		}
	}

	return { childAmbulances, adultAmbulances }
}

function buildOrderButtons(
	childAmbulances: IEmployeeAmbulance[],
	adultAmbulances: IEmployeeAmbulance[],
	employeeSlug: string,
	t: ReturnType<typeof useTranslations<'pages.team_detail.hero'>>
): OrderButton[] {
	const orderableAmbulances = [...childAmbulances, ...adultAmbulances]
	const totalCount = orderableAmbulances.length

	if (totalCount === 0) {
		return []
	}

	const childCount = childAmbulances.length
	const adultCount = adultAmbulances.length

	const getLabel = (
		ambulance: IEmployeeAmbulance,
		sectionTheme: SECTION_THEME_ENUM.CHILD | SECTION_THEME_ENUM.ADULT
	) => {
		if (totalCount === 1) {
			return t('createOrderOnline')
		}

		const categoryCount = sectionTheme === SECTION_THEME_ENUM.CHILD ? childCount : adultCount

		if (categoryCount === 1) {
			return sectionTheme === SECTION_THEME_ENUM.CHILD ? t('orderChild') : t('orderAdult')
		}

		const name = ambulance.name;

		if (name.length > 16) {
			return name;
		}

		return t('orderAmbulance', { name: name })
	}

	return [
		...adultAmbulances.map(ambulance => ({
			label: getLabel(ambulance, SECTION_THEME_ENUM.ADULT),
			url: `/volne-terminy?ambulance=${ambulance.slug}&employee=${employeeSlug}`
		})),
		...childAmbulances.map(ambulance => ({
			label: getLabel(ambulance, SECTION_THEME_ENUM.CHILD),
			url: `/volne-terminy?ambulance=${ambulance.slug}&employee=${employeeSlug}`
		}))
	]
}

const AEmployeeHeroButton = ({ employee }: { employee: IEmployeeExtended }) => {
	const t = useTranslations('pages.team_detail.hero')

	const isMedicalPersonnel = useMemo(() => {
		return employee.categories?.some(
			category => category.type === TEAM_CATEGORY_TYPE_ENUM.MEDICAL_PERSONNEL
		)
	}, [employee.categories])

	const orderButtons = useMemo(() => {
		if (!employee.ambulances?.length) {
			return []
		}

		const { childAmbulances, adultAmbulances } = partitionAmbulances(employee.ambulances)
		return buildOrderButtons(childAmbulances, adultAmbulances, employee.slug, t)
	}, [employee.ambulances, employee.slug, t])

	if (!isMedicalPersonnel || orderButtons.length === 0) {
		return null
	}

	return (
		<div className='flex flex-wrap gap-4 mgt-40 mgt-30lg'>
			{orderButtons.map(button => (
				<MButton key={button.url} variant='order' to={button.url} prefetch={false} className={'max-md:w-full'}>
					<span className='inline-flex items-center gap-2 min-w-0'>
						<CalendarIcon className='size-4 shrink-0' strokeWidth={2} aria-hidden />
						<span className={'truncate min-w-0'}>
							{button.label}
						</span>
					</span>
				</MButton>
			))}
		</div>
	)
}

export default AEmployeeHeroButton
